import React, { useEffect, useState } from "react";
import moment from "moment";
import { Card, CardContent, Typography, Button, Grid, Box, Menu, MenuItem } from "@mui/material";
import MenuBar from "./MenuBar";
import Footer from "./Footer";
import envVariable from "../EnvironmentVariables.json";
import { useNavigate } from "react-router-dom";
import { useNotificationListener } from "../utils/ws/useNotificationListener";
import "./Style.css";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  // Listen for real-time notifications via WebSocket
  useNotificationListener({
    onNotification: async (notification) => {
      console.log('[SCO-Notifications] New notification received:', notification);

      // Mark as seen immediately since user is on notifications page
      try {
        const userId = localStorage.getItem("User_Id");
        if (userId) {
          await fetch(`${envVariable.serverId}/SCO/getNotifications.php`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ notif_id: notification.notif_id }),
          });
          console.log('[SCO-Notifications] Marked notification as seen immediately');

          // Mark as seen locally
          notification.seen = 1;
        }
      } catch (error) {
        console.error('[SCO-Notifications] Failed to mark as seen:', error);
      }

      // Prepend new notification to the top of the list (already marked as seen)
      setNotifications(prevNotifs => [notification, ...prevNotifs]);

      // Refresh badge in MenuBar (should show 0 since we marked it as seen)
      if (window.refreshSCONotificationBadge) {
        window.refreshSCONotificationBadge();
      }
    },
  });

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Read userId fresh from localStorage
        const userId = localStorage.getItem("User_Id");
        if (!userId) {
          console.warn('[Notifications] No User_Id in localStorage, skipping fetch');
          return;
        }

        const response = await fetch(`${envVariable.serverId}/SCO/getNotifications.php?user_id=${userId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch notifications: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.status === "Success") {
          setNotifications(data.notifications);
          // Mark all notifications as seen when page is viewed
          await markAllNotificationsAsSeen();
          // Immediately refresh the badge in MenuBar
          if (window.refreshSCONotificationBadge) {
            window.refreshSCONotificationBadge();
          }
        } else {
          console.error(data.message);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, []);

  const markAllNotificationsAsSeen = async () => {
    try {
      // Read userId fresh from localStorage
      const userId = localStorage.getItem("User_Id");
      if (!userId) {
        console.warn('[Notifications] No User_Id in localStorage, skipping mark as seen');
        return;
      }

      const response = await fetch(`${envVariable.serverId}/SCO/markAllNotificationsSeen.php?user_id=${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.status === "Success") {
        // Update local state to reflect all notifications as seen
        setNotifications(prev => prev.map(notif => ({ ...notif, seen: 1 })));
      } else {
        console.error("Failed to mark all notifications as seen:", data.message);
      }
    } catch (error) {
      console.error("Error marking all notifications as seen:", error);
    }
  };

  const updateNotificationSeen = async (notif_id) => {
    try {
      const response = await fetch(`${envVariable.serverId}/SCO/getNotifications.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ notif_id }),
      });

      const data = await response.json();

      if (data.status !== "Success") {
        console.error("Failed to mark notification as seen:", data.message);
      }
    } catch (error) {
      console.error("Error marking notification as seen:", error);
    }
  };

  const handleFilterChange = (filterType) => {
    setFilter(filterType);
    setAnchorEl(null);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const filteredNotifications = filter === "Unread Only"
    ? notifications.filter((notif) => notif.seen === 0 || notif.seen === '0')
    : filter
    ? notifications.filter((notif) => notif.type === filter)
    : notifications;


  const fetchTaskDetails = async (taskId) => {
    try {
      const response = await fetch(`${envVariable.serverId}/SCO/updateTask.php?TaskId=${taskId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.status === "Success") {
        return data.data.task;
      } else {
        console.error("Failed to fetch task details:", data.message);
        return null;
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
      return null;
    }
  };

  // Parse datetime string from backend as UTC (backend stores all timestamps in UTC)
  const parseUTCDateTime = (dateString) => {
    if (!dateString) return null;
    // Backend returns format: "YYYY-MM-DD HH:MM:SS"
    // Replace space with T and add Z to indicate UTC
    const isoString = dateString.replace(' ', 'T') + (dateString.includes('Z') ? '' : 'Z');
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  };

  const handleViewDetails = async (notification) => {
    const { type, task_id, notif_id } = notification;

    updateNotificationSeen(notif_id);

    if (type === "New Message") {
      const task = await fetchTaskDetails(task_id);

      if (task) {
        // Parse UTC appointment_time and convert to local date for display
        const appointmentDate = parseUTCDateTime(task.appointment_time);
        const formattedDate = appointmentDate ? appointmentDate.toISOString().split("T")[0] : new Date().toISOString().split("T")[0];
        navigate(`/tasks/chat/${task_id}`, {
          state: {
            customerId: task.customer_id,
            customerName: task.Customer,
            mechanicId: task.mechanic_id,
            mechanicName: task.Mechanic,
            SC_Id: task.sc_id,
            originDate: formattedDate,
          },
        });
      }
    } else if (type.toLowerCase() === "updated task status") {
      navigate(`/tasks/edit-task/${task_id}`);
    } else if (type === "New Booking") {
      navigate("/booking-requests");
    }
  };

  const formatDescription = (notification) => {
    const { type, task_id, description, name } = notification;

    if (type === "New Message") {
      // Description already contains sender name from backend: "New message from {name}: {preview}"
      // Just append the task ID for context
      return `${description} (Task #${task_id})`;
    } else if (type.toLowerCase() === "updated task status") {
      const words = description.replace("\n", " ").split(" ");
      return `${name} Updated Task ${task_id} from ${words[0]} to ${words[1]}`;
    } else if (type === "New Booking") {
      return "New booking request available";
    }
    return description;
  };

  return (
    <>
      <MenuBar />
      <Box sx={{ p: 3 }}>
        <Button variant="contained" onClick={handleMenuClick}>
          Filter Notifications
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => handleFilterChange("")}>Last 30 Days</MenuItem>
          <MenuItem onClick={() => handleFilterChange("Unread Only")}>Unread Only</MenuItem>
          <MenuItem onClick={() => handleFilterChange("New Message")}>Messages Only</MenuItem>
          <MenuItem onClick={() => handleFilterChange("Updated Task Status")}>Updated Task Status</MenuItem>
          <MenuItem onClick={() => handleFilterChange("New Booking")}>New Bookings</MenuItem>
        </Menu>

        <Typography variant="h4" component="h2" gutterBottom>
          Notifications
        </Typography>
        <Grid container spacing={2}>
          {filteredNotifications.length === 0 ? (
            <Typography>No notifications available.</Typography>
          ) : (
            filteredNotifications.map((notif) => (
              <Grid item xs={12} sm={6} md={4} key={notif.notif_id}>
                <Card
                  sx={{
                    backgroundColor: notif.seen ? "#f0f0f0" : "#e3f2fd",
                    transition: "background-color 0.3s ease",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6">{notif.type}</Typography>
                    <Typography variant="body2">{formatDescription(notif)}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {moment.utc(notif.created_at).local().fromNow()}
                    </Typography>
                    <Box mt={2}>
                      <Button variant="contained" color="primary" onClick={() => handleViewDetails(notif)}>
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Box>
      <Footer />
    </>
  );
};

export default Notifications;
